/* CyberpunkGallery.module.css */

.page {
  min-height: 100vh;
  background: var(--bg-deep);
  padding: 0 24px 60px;
  position: relative;
  overflow-x: hidden;
}

/* ── Header ── */
.header {
  padding: 48px 0 40px;
  text-align: center;
  position: relative;
  z-index: 2;
}

.titleWrapper {
  margin-bottom: 24px;
}

/* Glitch title */
.title {
  font-family: var(--font-display);
  font-size: clamp(32px, 6vw, 72px);
  font-weight: 700;
  color: var(--neon-cyan);
  text-shadow: var(--glow-md) var(--neon-cyan);
  letter-spacing: 0.15em;
  text-transform: uppercase;
  position: relative;
  display: inline-block;
  margin: 0;
}

.glitchText {
  position: relative;
  display: inline-block;
}

.glitchText::before,
.glitchText::after {
  content: attr(data-text);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.glitchText::before {
  color: var(--neon-magenta);
  animation: glitchAnim 3s infinite linear alternate-reverse;
  clip-path: inset(0 0 60% 0);
  text-shadow: -2px 0 var(--neon-magenta);
}

.glitchText::after {
  color: var(--neon-cyan);
  animation: glitchAnim 2s infinite linear alternate;
  clip-path: inset(60% 0 0 0);
  text-shadow: 2px 0 var(--neon-cyan);
}

/* Subtitle line */
.titleLine {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 12px;
}

.lineAccent {
  display: inline-block;
  width: 60px;
  height: 1px;
  background: var(--neon-cyan);
  box-shadow: 0 0 4px var(--neon-cyan);
}

.lineText {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-muted);
  letter-spacing: 0.2em;
}

/* Meta row */
.metaRow {
  display: flex;
  justify-content: center;
  gap: 40px;
  flex-wrap: wrap;
}

.metaItem {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.metaLabel {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--text-muted);
  letter-spacing: 0.15em;
}

.metaValue {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-data);
  letter-spacing: 0.1em;
}

.pulseGreen {
  color: var(--neon-green) !important;
  animation: pulse 2s ease-in-out infinite;
}

.threatIndicator {
  display: inline-block;
  width: 8px;
  height: 8px;
  background: var(--neon-orange);
  border-radius: 50%;
  box-shadow: 0 0 6px var(--neon-orange);
  margin-right: 6px;
  animation: pulse 1.5s ease-in-out infinite;
}

/* ── Grid（loading / error / 空状态提示复用） ── */
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

.gridItem {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.grid.loaded .gridItem,
.timeline.loaded .gridItem {
  opacity: 1;
  transform: translateY(0);
  animation: fadeInUp 0.5s ease forwards;
}

/* ── Snake Timeline ── */
.timeline {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 0 60px;
  z-index: 2;
}

/* SVG 蛇形线 - 位于内容下层，只走边缘与间隙，不压卡片 */
.snakeSvg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  overflow: visible;
}

.snakePath {
  fill: none;
  stroke: var(--accent-cyan);
  stroke-width: 2;
  filter: drop-shadow(0 0 6px rgba(0, 240, 255, 0.5));
}

/* 时间线节点（同一日期的卡片组） */
.timelineItem {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  min-height: 320px;
}

/* 节点之间留出间隙，供蛇形线横向穿越 */
.timelineItem + .timelineItem {
  margin-top: 64px;
}

/* 左节点：锚点侧留 220px 走线区（竖线 50px + 日期线段），卡片铺满剩余宽度 */
.timelineItem.left {
  --anchor-top: 28%;
  justify-content: flex-start;
  padding-left: 220px;
  padding-right: 24px;
}

/* 右节点 */
.timelineItem.right {
  --anchor-top: 72%;
  justify-content: flex-end;
  padding-right: 220px;
  padding-left: 24px;
}

/* 节点内容包裹：日期 + 菱形 + 卡片组 */
.nodeContent {
  display: flex;
  align-items: center;
  position: relative;
}

/* 菱形节点标记 - 按锚点切入位置定位（上半/下半），不居中 */
.nodeDiamond {
  width: 16px;
  height: 16px;
  background: var(--accent-cyan);
  border: none;
  transform: rotate(45deg);
  box-shadow: 0 0 12px var(--accent-cyan);
  flex-shrink: 0;
  z-index: 20;
  position: absolute;
  top: var(--anchor-top);
  margin-top: -8px;
}
/* 卡片距容器边 220px，竖线在 50px → 菱形中心相对 nodeContent 偏移 -170px */
.timelineItem.left .nodeDiamond {
  left: -178px;
}
.timelineItem.right .nodeDiamond {
  right: -178px;
}

/* 日期标签 - 穿在锚点到卡片的横向线段上（线从文字中间穿过） */
.dateLabel {
  font-family: var(--font-code);
  font-size: 13px;
  color: var(--accent-green);
  letter-spacing: 1px;
  white-space: nowrap;
  position: absolute;
  top: var(--anchor-top);
  padding: 3px 10px;
  background: var(--bg-primary);
  border: 1px solid var(--border);
  border-left: 2px solid var(--accent-green);
  border-radius: 2px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.4);
  z-index: 21;
}

.dateLabel::before {
  content: '▸ ';
  color: var(--accent-green);
}

/* 日期水平居中于锚点→卡片线段的中点（竖线 50px，卡片边 220px → 中点 135px） */
.timelineItem.left .dateLabel {
  left: -85px;
  transform: translate(-50%, -50%);
}

.timelineItem.right .dateLabel {
  right: -85px;
  transform: translate(50%, -50%);
}

/* 卡片组（同一日期） - 自动换行 */
.cardsGroup {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
  max-width: 100%;
}

.timelineCard {
  width: 260px;
}

/* ── Infinite Scroll ── */
/* 哨兵：不可见，仅用于 IntersectionObserver 触发下一批加载 */
.sentinel {
  height: 1px;
  margin-top: 24px;
  pointer-events: none;
}

.loadMoreRow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
  margin-top: 8px;
  position: relative;
  z-index: 2;
}

.loadMoreBtn {
  font-family: var(--font-code);
  font-size: 13px;
  letter-spacing: 0.14em;
  color: var(--accent-cyan);
  background: transparent;
  border: 1px solid var(--accent-cyan);
  padding: 8px 20px;
  cursor: pointer;
  transition: box-shadow 0.2s ease, background 0.2s ease, opacity 0.2s ease;
}

.loadMoreBtn:hover {
  background: rgba(0, 240, 255, 0.08);
  box-shadow: 0 0 8px rgba(0, 240, 255, 0.35);
}

.loadMoreInfo {
  font-family: var(--font-code);
  font-size: 12px;
  color: var(--text-muted);
  letter-spacing: 0.15em;
}

/* ── Footer ── */
.footer {
  margin-top: 60px;
  text-align: center;
  position: relative;
  z-index: 2;
}

.footerLine {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
}

.footerDot {
  display: inline-block;
  width: 6px;
  height: 6px;
  background: var(--neon-cyan);
  border-radius: 50%;
  box-shadow: 0 0 6px var(--neon-cyan);
  animation: pulse 2s ease-in-out infinite;
}

.footerText {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-muted);
  letter-spacing: 0.25em;
}

.footerBarcode {
  display: flex;
  justify-content: center;
  gap: 3px;
}

.footerBar {
  display: inline-block;
  width: 3px;
  background: var(--text-muted);
  opacity: 0.4;
  border-radius: 1px;
}

/* ── Animations ── */
@keyframes glitchAnim {
  0% { clip-path: inset(40% 0 61% 0); transform: translate(-2px, 0); }
  20% { clip-path: inset(92% 0 1% 0); transform: translate(2px, 0); }
  40% { clip-path: inset(43% 0 1% 0); transform: translate(-1px, 0); }
  60% { clip-path: inset(25% 0 58% 0); transform: translate(1px, 0); }
  80% { clip-path: inset(54% 0 7% 0); transform: translate(-2px, 0); }
  100% { clip-path: inset(58% 0 43% 0); transform: translate(0); }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .page {
    padding: 0 16px 48px;
  }

  .header {
    padding: 32px 0 28px;
  }

  .metaRow {
    gap: 20px;
  }

  .grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
  }

  /* 小屏：放弃左右交替，统一左对齐，走线区收窄到 76px（竖线 24px + 日期段）。
     必须显式列出 .left/.right 与桌面规则同特异性（媒体查询不改变特异性），
     否则裸 .timelineItem (0,1,0) 会输给桌面的 .timelineItem.left (0,2,0) */
  .timeline {
    padding: 16px 0 40px;
  }

  .timelineItem.left,
  .timelineItem.right {
    min-height: 0;
    justify-content: flex-start;
    padding-left: 76px;
    padding-right: 0;
    --anchor-top: 36%;
  }

  .timelineItem + .timelineItem {
    margin-top: 48px;
  }

  .timelineItem.left .nodeDiamond,
  .timelineItem.right .nodeDiamond {
    left: -60px;
    right: auto;
  }

  .timelineItem.left .dateLabel,
  .timelineItem.right .dateLabel {
    left: -26px;
    right: auto;
    transform: translate(-50%, -50%);
  }

  .timelineCard {
    width: 100%;
  }

  .cardsGroup {
    width: 100%;
    gap: 12px;
  }

  .nodeContent {
    flex: 1;
    min-width: 0;
  }

  .loadMoreRow {
    gap: 12px;
  }

  .loadMoreBtn {
    padding: 8px 14px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .grid {
    grid-template-columns: 1fr;
  }
}

/* ── Reduced Motion ── */
@media (prefers-reduced-motion: reduce) {
  .glitchText::before,
  .glitchText::after {
    animation: none;
  }

  .glitchText::before {
    display: none;
  }

  .glitchText::after {
    display: none;
  }

  .gridItem {
    opacity: 1;
    transform: none;
    animation: none;
  }

  .grid.loaded .gridItem,
  .timeline.loaded .gridItem {
    animation: none;
    opacity: 1;
    transform: none;
  }

  .pulseGreen,
  .threatIndicator,
  .footerDot {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
}

/* ── HUD notices (loading / error / empty) ── */
.hudNotice {
  grid-column: 1 / -1;
  padding: 80px 24px;
  text-align: center;
  font-family: var(--font-mono);
  font-size: 15px;
  letter-spacing: 0.12em;
  color: var(--text-muted);
  border: 1px dashed rgba(0, 240, 255, 0.25);
  background: rgba(17, 17, 24, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.retryBtn {
  font-family: var(--font-mono);
  font-size: 13px;
  letter-spacing: 0.14em;
  color: var(--neon-cyan);
  background: transparent;
  border: 1px solid var(--neon-cyan);
  padding: 8px 20px;
  cursor: pointer;
  transition: box-shadow 0.2s ease, background 0.2s ease;
}

.retryBtn:hover {
  background: rgba(0, 240, 255, 0.08);
  box-shadow: var(--glow-sm) var(--neon-cyan);
}
